import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Empty, message } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#C0392B';

const TAB_KEYS = ['', 'live', 'upcoming', 'ended'];

function formatSchedule(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

function StatusBadge({ status, scheduledAt, t }) {
  if (status === 'live') {
    return (
      <span style={{
        position: 'absolute', top: 8, left: 8,
        background: RED, color: '#fff',
        fontSize: 11, fontWeight: 600,
        padding: '2px 8px', borderRadius: 4,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: '#fff', display: 'inline-block',
          animation: 'livePulse 1.2s ease-in-out infinite',
        }} />
        {t('live.streaming')}
      </span>
    );
  }
  if (status === 'upcoming') {
    return (
      <span style={{
        position: 'absolute', top: 8, left: 8,
        background: 'rgba(0,0,0,0.6)', color: '#fff',
        fontSize: 11, fontWeight: 500,
        padding: '2px 8px', borderRadius: 4,
      }}>
        {t('live.upcoming')} {formatSchedule(scheduledAt)}
      </span>
    );
  }
  if (status === 'ended') {
    return (
      <span style={{
        position: 'absolute', top: 8, left: 8,
        background: 'rgba(0,0,0,0.5)', color: '#ddd',
        fontSize: 11, fontWeight: 500,
        padding: '2px 8px', borderRadius: 4,
      }}>
        {t('live.replay')}
      </span>
    );
  }
  return null;
}

export default function LiveList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const TABS = [
    { key: '', label: t('common.all') },
    { key: 'live', label: t('live.streaming') },
    { key: 'upcoming', label: t('live.upcoming') },
    { key: 'ended', label: t('live.replay') },
  ];
  const [tab, setTab] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchList = useCallback(async (p = 1, append = false) => {
    if (p === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { page: p, limit: 20 };
      if (tab) params.status = tab;
      const res = await api.get('/live', { params });
      const data = res.data?.data || res.data?.rows || [];
      const total = res.data?.total ?? 0;
      if (append) {
        setItems(prev => [...prev, ...data]);
      } else {
        setItems(data);
      }
      setHasMore(p * 20 < total);
    } catch (e) {
      if (p === 1) setItems([]);
      message.error(t('live.loadFailed', '加载直播列表失败'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab]);

  useEffect(() => {
    setPage(1);
    fetchList(1, false);
  }, [fetchList]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchList(next, true);
  };

  const columns = isMobile ? 2 : 4;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 80 : 40 }}>
      {/* Pulse animation for live badge */}
      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1A1612 0%, #2E2418 60%, #1A1612 100%)',
        padding: isMobile ? '28px 16px 20px' : '36px 24px 28px',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: isMobile ? 22 : 28, fontWeight: 700,
          color: '#fff', letterSpacing: 3, marginBottom: 4,
        }}>
          {t('live.liveZone', '直播专区')}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', letterSpacing: 1 }}>
          {t('live.subtitle', '实时互动 · 好物直购')}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: `1px solid ${BD}`,
        background: '#fff',
        position: 'sticky', top: 0, zIndex: 10,
        maxWidth: 1200, margin: '0 auto',
      }}>
        {TABS.map(tabItem => {
          const active = tab === tabItem.key;
          return (
            <div
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              style={{
                flex: 1, textAlign: 'center',
                padding: '12px 0', cursor: 'pointer',
                fontSize: 14, fontWeight: active ? 600 : 400,
                color: active ? GOLD : MUTED,
                borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              {tabItem.label}
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '20px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Empty
              description={
                <span style={{ color: MUTED, fontSize: 14 }}>
                  {tab === 'live' ? t('live.emptyLive', '暂无正在进行的直播') :
                   tab === 'upcoming' ? t('live.emptyUpcoming', '暂无预告直播') :
                   tab === 'ended' ? t('live.emptyEnded', '暂无回放内容') :
                   t('live.emptyAll', '暂无直播内容')}
                </span>
              }
            />
          </div>
        ) : (
          <>
            {/* Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: isMobile ? 10 : 16,
            }}>
              {items.map(item => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/live/${item.id}`)}
                  style={{
                    background: '#fff',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: `1px solid ${BD}`,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  {/* Cover image 16:9 */}
                  <div style={{
                    position: 'relative',
                    paddingTop: '56.25%',
                    background: '#E8E0D0',
                    overflow: 'hidden',
                  }}>
                    {item.cover_url ? (
                      <img
                        src={item.cover_url}
                        alt={item.title}
                        style={{
                          position: 'absolute', top: 0, left: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: MUTED, fontSize: 32,
                      }}>
                        LIVE
                      </div>
                    )}
                    <StatusBadge status={item.status} scheduledAt={item.scheduled_at} t={t} />
                    {/* Viewer count overlay */}
                    {item.status === 'live' && item.viewer_count > 0 && (
                      <span style={{
                        position: 'absolute', bottom: 6, right: 6,
                        background: 'rgba(0,0,0,0.55)', color: '#fff',
                        fontSize: 10, padding: '1px 6px', borderRadius: 3,
                      }}>
                        {item.viewer_count > 9999
                          ? `${(item.viewer_count / 10000).toFixed(1)}${t('live.wan', '万')}`
                          : item.viewer_count} {t('live.watching', '观看')}
                      </span>
                    )}
                    {/* Product count badge */}
                    {item.product_count > 0 && (
                      <span style={{
                        position: 'absolute', top: 8, right: 8,
                        background: GOLD, color: '#fff',
                        fontSize: 10, fontWeight: 600,
                        padding: '2px 6px', borderRadius: 3,
                      }}>
                        {item.product_count} {t('live.products', '件商品')}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ padding: isMobile ? '8px 10px 10px' : '10px 12px 12px' }}>
                    <div style={{
                      fontSize: isMobile ? 13 : 14, fontWeight: 600,
                      color: INK, lineHeight: 1.4,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {item.title || t('live.defaultRoom', '直播间')}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 4,
                    }}>
                      <span style={{ fontSize: 12, color: MUTED }}>
                        {item.host_name || item.merchant_name || t('live.defaultHost', '主播')}
                      </span>
                      {item.status === 'ended' && item.total_viewers > 0 && (
                        <span style={{ fontSize: 11, color: '#B0A898' }}>
                          {item.total_viewers} {t('live.watched', '已观看')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    background: 'none', border: `1px solid ${BD}`,
                    color: GOLD, fontSize: 14, padding: '8px 32px',
                    borderRadius: 20, cursor: 'pointer',
                  }}
                >
                  {loadingMore ? t('common.loading') : t('browse.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
